import { useState } from 'react';
import { useChips, usePortfolio, useGameInfo } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Zap, 
  Shield, 
  TrendingUp,
  AlertCircle,
  Check,
  X,
  Sparkles,
  RefreshCw,
  Lock,
  Target
} from 'lucide-react';

const ChipsPage = () => {
  const { chipStatus, chipInfo, activeChip, loading, activateChip, deactivateChip, refresh } = useChips();
  const { portfolio } = usePortfolio();
  const { gameInfo } = useGameInfo();
  
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [selectedChip, setSelectedChip] = useState(null);
  const [targetSymbol, setTargetSymbol] = useState('');
  const [processing, setProcessing] = useState(false);

  const chipIcons = {
    wildcard: Zap,
    stop_loss_shield: Shield,
    leverage_boost: TrendingUp
  };

  const chipColors = {
    wildcard: 'from-purple-500/20 to-indigo-500/20 border-purple-500/30',
    stop_loss_shield: 'from-emerald-500/20 to-teal-500/20 border-emerald-500/30',
    leverage_boost: 'from-amber-500/20 to-orange-500/20 border-amber-500/30'
  };

  const getChipRemaining = (chipType) => {
    if (!chipStatus) return 0;
    switch (chipType) {
      case 'wildcard':
        return chipStatus.wildcards_remaining;
      case 'stop_loss_shield':
        return chipStatus.stop_loss_shields_remaining;
      case 'leverage_boost':
        return chipStatus.leverage_boosts_remaining;
      default:
        return 0;
    }
  };

  const openActivateModal = (chipType) => {
    setSelectedChip(chipType);
    setTargetSymbol('');
    setActivateModalOpen(true);
  };

  const handleActivate = async () => {
    if (!selectedChip) return;
    
    const chipConfig = chipInfo?.chips?.[selectedChip];
    if (chipConfig?.requires_target && !targetSymbol) {
      toast.error('Please select a target instrument');
      return;
    }

    setProcessing(true);
    try {
      await activateChip(selectedChip, targetSymbol || null);
      toast.success(`${chipConfig?.name} activated for next week!`);
      setActivateModalOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Cancel activated chip? You can re-activate before Sunday.')) return;
    
    setProcessing(true);
    try {
      await deactivateChip();
      toast.success('Chip deactivated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-pulse text-emerald-400">Loading chips...</div>
      </div>
    );
  }

  const chips = chipInfo?.chips || {};

  return (
    <div className="min-h-screen bg-slate-950 pb-20" data-testid="chips-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <Sparkles className="h-8 w-8 text-purple-500" />
                Power-Up Chips
              </h1>
              <p className="text-slate-400 mt-1">
                Special abilities to boost your strategy. Use wisely!
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={refresh}
              className="border-slate-600 text-slate-300"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Active Chip Banner */}
        {chipStatus?.active_chip_this_week && (
          <div className="mb-8 p-4 rounded-xl bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  {chipIcons[chipStatus.active_chip_this_week] && 
                    <div className="text-purple-400">
                      {(() => {
                        const Icon = chipIcons[chipStatus.active_chip_this_week];
                        return <Icon className="h-6 w-6" />;
                      })()}
                    </div>
                  }
                </div>
                <div>
                  <p className="text-purple-400 font-semibold text-lg">
                    {chips[chipStatus.active_chip_this_week]?.name} Activated
                  </p>
                  <p className="text-slate-400 text-sm">
                    Active for Week {gameInfo?.current_week + 1}
                    {chipStatus.active_chip_target && (
                      <span className="ml-2">
                        Target: <span className="text-amber-400">{chipStatus.active_chip_target}</span>
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={handleDeactivate}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                disabled={processing}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Rules Card */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Chip Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <Check className="h-4 w-4 text-emerald-500 mt-0.5" />
                <span className="text-slate-300">One chip per week maximum</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="h-4 w-4 text-emerald-500 mt-0.5" />
                <span className="text-slate-300">Activate Wed-Sat for next week</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="h-4 w-4 text-emerald-500 mt-0.5" />
                <span className="text-slate-300">Can cancel before Sunday 00:00 UTC</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chips Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {Object.entries(chips).map(([chipType, config]) => {
            const Icon = chipIcons[chipType];
            const remaining = getChipRemaining(chipType);
            const isAvailable = remaining > 0;
            const isActive = chipStatus?.active_chip_this_week === chipType;
            
            return (
              <Card 
                key={chipType}
                className={`bg-gradient-to-br ${chipColors[chipType]} border transition-all hover:scale-[1.02] ${
                  !isAvailable ? 'opacity-60' : ''
                }`}
                data-testid={`chip-card-${chipType}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="w-14 h-14 rounded-xl bg-slate-900/50 flex items-center justify-center">
                      {Icon && <Icon className="h-7 w-7 text-white" />}
                    </div>
                    <Badge 
                      className={`${isAvailable ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'}`}
                    >
                      {remaining} left
                    </Badge>
                  </div>
                  <CardTitle className="text-white mt-4">{config.name}</CardTitle>
                  <CardDescription className="text-slate-300">
                    {config.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 rounded-lg bg-slate-900/50">
                      <p className="text-slate-400 text-xs uppercase mb-1">Effect</p>
                      <p className="text-white text-sm">{config.effect}</p>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">Uses per season:</span>
                      <span className="text-white">{config.uses_per_season}</span>
                    </div>

                    {config.requires_target && (
                      <div className="flex items-center gap-2 text-sm text-amber-400">
                        <Target className="h-4 w-4" />
                        <span>Requires target instrument</span>
                      </div>
                    )}

                    <Button
                      onClick={() => openActivateModal(chipType)}
                      disabled={!isAvailable || isActive || chipStatus?.active_chip_this_week}
                      className={`w-full ${
                        isAvailable && !isActive && !chipStatus?.active_chip_this_week
                          ? 'bg-white/10 hover:bg-white/20 text-white'
                          : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid={`activate-${chipType}-btn`}
                    >
                      {!isAvailable ? (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          No chips remaining
                        </>
                      ) : isActive ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Activated
                        </>
                      ) : chipStatus?.active_chip_this_week ? (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          Another chip active
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 mr-2" />
                          Activate for Next Week
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Usage History */}
        {chipStatus?.chips_used?.length > 0 && (
          <Card className="bg-slate-900 border-slate-800 mt-8">
            <CardHeader>
              <CardTitle className="text-white">Chip History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {chipStatus.chips_used.map((usage, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      {chipIcons[usage.chip_type] && (
                        <div className="text-slate-400">
                          {(() => {
                            const Icon = chipIcons[usage.chip_type];
                            return <Icon className="h-5 w-5" />;
                          })()}
                        </div>
                      )}
                      <span className="text-white">{chips[usage.chip_type]?.name}</span>
                    </div>
                    <span className="text-slate-400 text-sm">Week {usage.week}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Activate Modal */}
      <Dialog open={activateModalOpen} onOpenChange={setActivateModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Activate {chips[selectedChip]?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              This chip will be active for Week {gameInfo?.current_week + 1}
            </DialogDescription>
          </DialogHeader>
          
          {selectedChip && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-slate-800">
                <p className="text-slate-400 text-sm mb-2">Effect:</p>
                <p className="text-white">{chips[selectedChip]?.effect}</p>
              </div>

              {chips[selectedChip]?.requires_target && (
                <div className="space-y-2">
                  <label className="text-slate-300 text-sm">Select Target Instrument</label>
                  <Select value={targetSymbol} onValueChange={setTargetSymbol}>
                    <SelectTrigger className="bg-slate-800 border-slate-600">
                      <SelectValue placeholder="Choose an instrument from your portfolio" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {portfolio?.holdings?.map((holding) => (
                        <SelectItem 
                          key={holding.symbol} 
                          value={holding.symbol}
                          className="text-white hover:bg-slate-700"
                        >
                          {holding.symbol}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-slate-500 text-xs">
                    The 2x multiplier will apply to this instrument's score
                  </p>
                </div>
              )}

              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm">
                  <AlertCircle className="inline h-4 w-4 mr-1" />
                  Remember: Only ONE chip can be active per week!
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setActivateModalOpen(false)}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleActivate}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={processing || (chips[selectedChip]?.requires_target && !targetSymbol)}
              data-testid="confirm-activate-btn"
            >
              {processing ? 'Activating...' : 'Activate Chip'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChipsPage;
